import React, { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { useAppContext } from '../AppContext';
import * as LucideIcons from 'lucide-react';
import { useTranslation } from 'react-i18next';

const GiftModal = ({ author, whisper, onClose }) => {
    const { appFunctions, setMessage, currentUserProfile } = useAppContext();
    const { t } = useTranslation();
    const [amount, setAmount] = useState(50);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const presetAmounts = [10, 50, 250, 1000];

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (amount <= 0) {
            setMessage(t('giftModal.positiveAmountError'));
            return;
        }
        if (currentUserProfile.tokens < amount) {
            setMessage(t('giftModal.notEnoughEchoesError'));
            return;
        }
        setIsSubmitting(true);
        try {
            const tipAuthor = httpsCallable(appFunctions, 'tipAuthor');
            await tipAuthor({
                authorId: author.id,
                tipAmount: amount,
                whisperId: whisper.id
            });
            setMessage(t('giftModal.giftSuccess', { amount, displayName: author.displayName }));
            onClose();
        } catch (error) {
            setMessage(t('giftModal.giftError', { message: error.message }));
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex justify-center items-center p-4" style={{ zIndex: 'var(--z-index-modal-backdrop)' }}>
            <div className="bg-gray-900/80 backdrop-blur-md p-6 rounded-lg shadow-glow max-w-md w-full text-white relative border border-yellow-500/50">
                <div className="text-center">
                    <img src={author.photoURL || "https://placehold.co/80x80/AEC6CF/FFFFFF?text=U"} alt={author.displayName} className="w-20 h-20 rounded-full mx-auto mb-4 border-4 border-yellow-400" />
                    <h3 className="text-xl font-bold mb-2 text-yellow-300 font-playfair">{t('giftModal.title', { displayName: author.displayName })}</h3>
                    <p className="text-gray-300 mb-6">{t('giftModal.description')}</p>
                </div>
                
                <form onSubmit={handleSubmit}>
                    <div className="grid grid-cols-4 gap-3 mb-6">
                        {presetAmounts.map(preset => (
                            <button
                                type="button"
                                key={preset}
                                onClick={() => setAmount(preset)}
                                className={`px-2 py-3 rounded-lg font-bold transition-all ${amount === preset ? 'bg-yellow-500 text-black ring-2 ring-yellow-300' : 'bg-gray-800/60 hover:bg-gray-700/80'}`}
                            >
                                {preset}
                            </button>
                        ))}
                    </div>

                    <div className="mb-4">
                        <label htmlFor="custom-amount" className="block text-sm font-medium text-gray-400 mb-2">{t('giftModal.customAmountLabel')}</label>
                        <input
                            type="number"
                            id="custom-amount"
                            value={amount}
                            onChange={(e) => setAmount(Number(e.target.value))}
                            className="w-full p-3 bg-gray-800/60 border border-gray-700 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-yellow-500 text-center font-bold text-lg"
                            min="1"
                        />
                    </div>
                    
                    <div className="text-center text-sm text-gray-400 mb-6">
                        {t('giftModal.balance', { tokens: currentUserProfile.tokens || 0 })}
                    </div>

                    <button type="submit" disabled={isSubmitting} className="w-full px-4 py-3 bg-yellow-600 text-black font-bold rounded-lg hover:bg-yellow-500 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                        {isSubmitting ? t('giftModal.sending') : t('giftModal.sendButton', { amount })}
                        <LucideIcons.Gift size={18} />
                    </button>
                </form>

                <button onClick={onClose} className="absolute top-3 right-3 p-2 rounded-full bg-gray-700/50 hover:bg-gray-600/50 transition duration-300" aria-label={t('giftModal.close')}>
                    <LucideIcons.X size={20} />
                </button>
            </div>
        </div>
    );
};

export default GiftModal;
